import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { CheckCircle, Loader2 } from 'lucide-react';

const SubscriptionSuccess = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [checking, setChecking] = useState(true);
  const [success, setSuccess] = useState(false);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    } else {
      navigate('/subscription');
    }
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    let attempts = 0;
    const maxAttempts = 5;
    const pollInterval = 2000;

    const poll = async () => {
      if (attempts >= maxAttempts) {
        toast.error('Payment verification timed out. Please check your subscription status.');
        setChecking(false);
        return;
      }

      try {
        const response = await axios.get(`/payments/status/${sessionId}`);
        
        if (response.data.payment_status === 'paid') {
          setSuccess(true);
          setChecking(false);
          toast.success('Subscription activated!');
          setTimeout(() => {
            window.location.href = '/dashboard';
          }, 2000);
          return;
        }

        if (response.data.status === 'expired') {
          toast.error('Payment session expired');
          setChecking(false);
          return;
        }

        attempts++;
        setTimeout(poll, pollInterval);
      } catch (error) {
        toast.error('Failed to verify payment');
        setChecking(false);
      }
    };

    poll();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 to-slate-100 dark:from-slate-900 dark:to-slate-950 flex items-center justify-center p-6" data-testid="subscription-success-page">
      <div className="medical-card p-12 text-center max-w-md">
        {checking ? (
          <>
            <Loader2 className="w-16 h-16 text-primary animate-spin mx-auto mb-6" />
            <h2 className="text-2xl font-bold font-['Manrope'] mb-2">Verifying Payment</h2>
            <p className="text-muted-foreground">Please wait while we confirm your subscription...</p>
          </>
        ) : success ? (
          <>
            <CheckCircle className="w-16 h-16 text-success mx-auto mb-6" data-testid="success-icon" />
            <h2 className="text-2xl font-bold font-['Manrope'] mb-2">Subscription Activated!</h2>
            <p className="text-muted-foreground mb-6">
              You now have full access to all ClinicalSim AI features. Redirecting you to the dashboard...
            </p>
            <Button data-testid="go-to-dashboard-button" onClick={() => navigate('/dashboard')} className="bg-[#0F766E] hover:bg-[#0d6960] text-white">
              Go to Dashboard
            </Button>
          </>
        ) : (
          <>
            <h2 className="text-2xl font-bold font-['Manrope'] mb-2">Payment Verification</h2>
            <p className="text-muted-foreground mb-6">
              We couldn't verify your payment at this time. Please check your subscription status in your dashboard.
            </p>
            <Button data-testid="back-to-dashboard-button" onClick={() => navigate('/dashboard')} className="bg-[#0F766E] hover:bg-[#0d6960] text-white">
              Back to Dashboard
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default SubscriptionSuccess;